<template>
  <div class="widget-field-phone">
    <div
      class="widget-field-phone__wrapper"
      :class="{
        'widget-field-phone__wrapper--focused': focused,
        'widget-field-phone__wrapper--error': hasError,
      }"
    >
      <widget-field-phone-country
        :model-value="countryCode"
        :readonly="readonly"
        :available-countries="availableCountries"
        :focused="focused"
        @toggle="focused = $event || focused"
        @update:model-value="onSelectCountry"
      />

      <widget-field-text
        ref="phoneRef"
        :model-value="phoneNumber"
        :readonly="readonly"
        :title="phoneNumber"
        class="widget-field-phone__phone"
        :placeholder="placeholder"
        @update:model-value="onInputPhone"
        @focus="focused = true"
        @blur="onBlurPhone"
      />
    </div>

    <widget-field-error
      v-if="hasError"
      class="pt-1"
      :text="errorMessage"
    />
  </div>
</template>

<script lang="ts">
import { libPhoneNumberService } from '@widgets/shared/services/libPhoneNumberService'
import { countryPhoneService } from '@widgets/shared/services/countryPhoneService'
import WidgetFieldPhoneCountry from '@widgets/shared/components/fields/WidgetFieldPhoneCountry.vue'
import WidgetFieldText from '@widgets/shared/components/fields/WidgetFieldText.vue'
import WidgetFieldError from '@widgets/shared/components/fields/WidgetFieldError.vue'
import { phoneService } from '@widgets/shared/services/phoneService'
import type { PropType } from '@/composition/vue/compositionApi'
import { computed, defineComponent, ref } from '@/composition/vue/compositionApi'
import { type TmCountryCode, TmFakeCountryCode } from '@/services/types'

export default defineComponent({
  components: {
    WidgetFieldError,
    WidgetFieldText,
    WidgetFieldPhoneCountry,
  },
  props: {
    phoneNumber: {
      type: String,
      required: true,
    },
    countryCode: {
      type: String as PropType<TmCountryCode>,
      required: true,
    },
    availableCountries: {
      type: Array as PropType<TmCountryCode[]>,
      default: () => [],
    },
    readonly: {
      type: Boolean,
    },
    hasError: {
      type: Boolean,
    },
    errorMessage: {
      type: String,
    },
  },
  emits: {
    // eslint-disable-next-line @typescript-eslint/naming-convention
    'update:phone-number': (value: string) => true,
    // eslint-disable-next-line @typescript-eslint/naming-convention
    'update:country-code': (value: TmCountryCode) => true,
    focus: () => true,
    blur: () => true,
  },
  setup(props, context) {
    const phoneRef = ref<InstanceType<typeof WidgetFieldText>>()
    const focused = ref(false)

    const placeholder = computed(() => {
      if (props.countryCode === TmFakeCountryCode) return ''
      return countryPhoneService.getRandomizedPhoneNumber(props.countryCode)
    })

    const onSelectCountry = async (value: TmCountryCode) => {
      const phonePrefix = value === TmFakeCountryCode ? '' : countryPhoneService.getPhonePrefix(value)

      context.emit('update:country-code', value)
      context.emit('update:phone-number', phonePrefix)

      phoneRef.value?.focus()
    }

    const tryUpdateCountryCode = (phoneNumber: string) => {
      if (phoneService.isLocalPhoneFormat(phoneNumber)) return
      let newCountryCode = libPhoneNumberService.getCountryByPhone(phoneNumber)
      if (!newCountryCode) newCountryCode = phoneService.getExceptionalPhoneCountryCode(phoneNumber)
      if (!newCountryCode) return
      if (props.availableCountries.length && !props.availableCountries.includes(newCountryCode)) return
      context.emit('update:country-code', newCountryCode)
    }

    const onInputPhone = (phoneNumber: string) => {
      context.emit('update:phone-number', phoneNumber)
      tryUpdateCountryCode(phoneNumber)
    }

    const onBlurPhone = () => {
      focused.value = false
      context.emit('blur')
    }

    return {
      phoneRef,
      focused,
      placeholder,
      onSelectCountry,
      onInputPhone,
      onBlurPhone,
    }
  },
})
</script>

<style lang="scss" scoped>
.widget-field-phone {
  &__wrapper {
    position: relative;
    display: flex;
    align-items: center;
    border-radius: $border-radius;
    background: $white;
    transition: background $transition-300ms-ease-in-out;
    cursor: pointer;

    &::before {
      content: '';
      position: absolute;
      inset: 0;
      z-index: 5;
      border: 1px solid $gray4;
      border-radius: $border-radius;
      pointer-events: none;
      transition: border-color $transition-300ms-ease-in-out;
    }

    &--focused {
      &:before {
        border: 2px solid $blue400;
      }
    }

    &--error {
      &:before {
        border-color: $error;
      }
    }
  }

  &__phone {
    width: 100%;

    :deep(.q-field__control) {
      &::before,
      &::after {
        border: 0 !important;
      }
      input {
        padding-left: 8px;
      }
    }
  }
}
</style>
